import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Activity, RefreshCw, Circle, Mail, Send } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const roleLabel = { admin: "Administrator", projektleder: "Projektleder", medarbejder: "Medarbejder" };
const deptLabel = { salg: "Salg", indkoeb: "Indkøb", kvalitet: "Kvalitet", produktion: "Produktion" };
const initials = (n) => (n || "").split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]?.toUpperCase()).join("") || "?";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("da-DK", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

function statusOf(iso, now) {
  if (!iso) return { key: "aldrig", label: "Aldrig aktiv", tone: { bg: "#F3F1EC", tx: "#78716C", bd: "#E7E5E4", dot: "#A8A29E" } };
  const diffMin = (now - new Date(iso).getTime()) / 60000;
  if (diffMin <= 15) return { key: "nu", label: "Aktiv nu", tone: { bg: "#E7EADF", tx: "#7E856C", bd: "#D3D8C6", dot: "#7E856C" } };
  if (new Date(iso).toDateString() === new Date(now).toDateString())
    return { key: "idag", label: "Aktiv i dag", tone: { bg: "#F4EFEC", tx: "#806B61", bd: "#E6DDD6", dot: "#A08979" } };
  const days = Math.max(1, Math.floor(diffMin / 1440));
  return { key: "inaktiv", label: `Inaktiv i ${days} ${days === 1 ? "dag" : "dage"}`, tone: { bg: "#F3F1EC", tx: "#78716C", bd: "#E7E5E4", dot: "#A8A29E" } };
}

export default function AdminActivity() {
  const [rows, setRows] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(false);
  const [wr, setWr] = useState(null);
  const [wrSaving, setWrSaving] = useState(false);
  const [wrTesting, setWrTesting] = useState(false);
  useEffect(() => { api.get("/weekly-report/settings").then((r) => setWr(r.data)).catch(() => {}); }, []);
  const saveWr = () => { setWrSaving(true); api.put("/weekly-report/settings", { enabled: wr.enabled, start_date: wr.start_date }).then((r) => { setWr(r.data); toast.success("Indstillinger gemt"); }).catch((e) => toast.error(e?.response?.data?.detail || "Kunne ikke gemme")).finally(() => setWrSaving(false)); };
  const testWr = () => { setWrTesting(true); api.post("/weekly-report/send-test").then((r) => toast.success(`Testmail sendt til ${r.data.sent_to}`)).catch((e) => toast.error(e?.response?.data?.detail || "Kunne ikke sende test")).finally(() => setWrTesting(false)); };

  const load = () => { setLoading(true); api.get("/admin/activity").then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setInterval(() => { setNow(Date.now()); load(); }, 20000); return () => clearInterval(t); }, []);

  const withStatus = useMemo(() => (rows || []).map((u) => ({ ...u, st: statusOf(u.last_seen_at, now) })), [rows, now]);
  const counts = useMemo(() => {
    const c = { nu: 0, idag: 0, inaktiv: 0, aldrig: 0 };
    withStatus.forEach((u) => { c[u.st.key] += 1; });
    return c;
  }, [withStatus]);

  const KPI = ({ label, value, dot }) => (
    <div className="pcard p-5 flex items-center gap-4" data-testid={`activity-kpi-${label}`}>
      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: dot }} />
      <div>
        <p className="text-3xl font-light text-[#1A1919] leading-none">{value}</p>
        <p className="label-caps mt-2">{label}</p>
      </div>
    </div>
  );

  return (
    <div className="rise-in max-w-[1200px]" data-testid="admin-activity-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="label-caps mb-2">Ledelse</p>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-[#1A1919]">Aktivitetslog</h1>
          <p className="text-stone-500 mt-2 text-sm">Følg hvornår brugerne senest har været aktive i portalen. Opdateres automatisk hvert 20. sekund.</p>
        </div>
        <Button variant="outline" onClick={() => { setNow(Date.now()); load(); }} disabled={loading} className="rounded-xl border-[#EBE8E3] bg-white shrink-0" data-testid="activity-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Opdatér
        </Button>
      </div>

      {wr && (
        <div className="pcard p-6 mb-6" data-testid="weekly-report-card">
          <div className="flex items-start gap-3 mb-4">
            <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#F1DEDA" }}><Mail className="h-5 w-5" style={{ color: "#A83232" }} /></span>
            <div>
              <h2 className="text-lg font-semibold text-[#1A1919]">Ugentlig mandagsmail</h2>
              <p className="text-sm text-stone-500">Sendes automatisk hver mandag kl. 07.00 til projektledere i salg (status) og administratorer (ledelsesoverblik). Kunder modtager aldrig denne mail.</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Switch checked={!!wr.enabled} onCheckedChange={(v) => setWr({ ...wr, enabled: v })} data-testid="wr-enabled-switch" />
              <span className="text-sm font-medium text-[#1A1919]">{wr.enabled ? "Aktiveret" : "Deaktiveret"}</span>
            </div>
            <div>
              <label className="label-caps block mb-1.5">Første udsendelse fra</label>
              <Input type="date" value={wr.start_date || ""} onChange={(e) => setWr({ ...wr, start_date: e.target.value })} className="rounded-xl border-[#EBE8E3] bg-white w-[180px]" data-testid="wr-start-date" />
            </div>
            <Button onClick={saveWr} disabled={wrSaving} className="rounded-xl text-white" style={{ background: "#A83232" }} data-testid="wr-save-btn">{wrSaving ? "Gemmer…" : "Gem"}</Button>
            <Button variant="outline" onClick={testWr} disabled={wrTesting} className="rounded-xl border-[#EBE8E3] bg-white" data-testid="wr-test-btn"><Send className="h-4 w-4 mr-1.5" /> {wrTesting ? "Sender…" : "Send test til mig"}</Button>
          </div>
          <p className="text-xs text-stone-400 mt-3">Mails udsendes først fra den valgte dato. Den er sat frem i tiden, så der ikke udsendes før portalen er introduceret.</p>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPI label="Aktiv nu" value={counts.nu} dot="#7E856C" />
        <KPI label="Aktiv i dag" value={counts.idag} dot="#A08979" />
        <KPI label="Inaktive" value={counts.inaktiv} dot="#A8A29E" />
        <KPI label="Aldrig aktiv" value={counts.aldrig} dot="#A83232" />
      </div>

      {rows === null ? (
        <div className="text-stone-400 text-sm py-16 text-center">Indlæser…</div>
      ) : (
        <div className="space-y-2.5" data-testid="activity-list">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 pb-1">
            <div className="col-span-4 label-caps">Bruger</div>
            <div className="col-span-2 label-caps">Rolle</div>
            <div className="col-span-2 label-caps">Sidste login</div>
            <div className="col-span-2 label-caps">Sidst aktiv</div>
            <div className="col-span-2 label-caps text-right">Status</div>
          </div>
          {withStatus.map((u) => (
            <div key={u.id} data-testid={`activity-row-${u.id}`} className="pcard grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 md:items-center p-4 md:px-5 md:py-4">
              <div className="md:col-span-4 flex items-center gap-3 min-w-0">
                {u.avatar_url
                  ? <img src={`${BACKEND}${u.avatar_url}`} alt="" className="h-10 w-10 rounded-full object-cover shrink-0 border border-[#EBE8E3]" />
                  : <span className="h-10 w-10 rounded-full shrink-0 flex items-center justify-center text-sm font-semibold" style={{ background: "#F1DEDA", color: "#A83232" }}>{initials(u.name)}</span>}
                <div className="min-w-0">
                  <p className="font-semibold text-[#1A1919] truncate">{u.name}{!u.active && <span className="ml-2 text-xs text-stone-400">(deaktiveret)</span>}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  <p className="text-xs text-stone-400 truncate">{(u.departments || []).map((d) => deptLabel[d] || d).join(", ") || "—"}</p>
                </div>
              </div>
              <div className="md:col-span-2 text-sm text-stone-600"><span className="md:hidden label-caps mr-2">Rolle</span>{roleLabel[u.role] || u.role}</div>
              <div className="md:col-span-2 text-sm text-stone-600"><span className="md:hidden label-caps mr-2">Sidste login</span>{fmt(u.last_login_at)}</div>
              <div className="md:col-span-2 text-sm text-stone-600"><span className="md:hidden label-caps mr-2">Sidst aktiv</span>{fmt(u.last_seen_at)}</div>
              <div className="md:col-span-2 md:text-right">
                <span data-testid={`activity-status-${u.id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: u.st.tone.bg, color: u.st.tone.tx, border: `1px solid ${u.st.tone.bd}` }}>
                  <Circle className="h-1.5 w-1.5" fill={u.st.tone.dot} strokeWidth={0} /> {u.st.label}
                </span>
              </div>
            </div>
          ))}
          {withStatus.length === 0 && <div className="pcard p-14 text-center text-stone-500" data-testid="activity-empty">Ingen brugere fundet.</div>}
        </div>
      )}
    </div>
  );
}
